// app/src/lib/backup/escrow.js — recovery-phrase escrow of the device crypto salt.
//
// The backup key is HKDF(cognito sub, per-device salt): deterministic, but the
// salt never leaves the device — so before this module, losing the phone meant
// the S3 snapshot was undecryptable forever. The escrow closes that hole
// WITHOUT weakening the threat model ("attacker can read the bucket"): we
// upload the salt wrapped by a key derived from a recovery phrase only the
// operator knows. Bucket contents alone still yield nothing; phrase + bucket +
// the user's own sign-in re-derives the exact backup key on a new device.
//
//   setup:    wrapSalt(phrase, deviceSalt)  → JSON blob → PUT {userId}/salt-escrow.json
//   recover:  GET blob → unwrapSalt(phrase) → install salt locally → performRestore()
//
// KDF is PBKDF2-SHA-256 at 600k iterations (OWASP 2023 floor) via SubtleCrypto —
// zero dependencies. The GCM AAD binds the blob to the userId, so a blob swapped
// across prefixes fails closed.

import { encrypt, decrypt, b64uEncode, b64uDecode, deriveKeys } from '../crypto.js';
import * as kv from '../idbKv.js';
import { db } from '../db.js';
import { getAccessToken, currentClaims } from '../auth/cognitoClient.js';
import { getConfig } from '../config.js';

const PBKDF2_ITERS = 600_000;
const SALT_KEY = 'kanjo.crypto.salt.v1'; // must match crypto.js getOrCreateSalt
const MIN_PHRASE_LEN = 8;

const enc = new TextEncoder();

async function phraseKey(phrase, kdfSalt, iters) {
  const ikm = await crypto.subtle.importKey('raw', enc.encode(phrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: kdfSalt, iterations: iters },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

const aadFor = (userId) => enc.encode(`kanjo/salt-escrow/v1|${userId}`);

/** Wrap the device salt under a recovery phrase. Pure crypto — no I/O. */
export async function wrapSalt(phrase, deviceSalt, userId) {
  if (typeof phrase !== 'string' || phrase.trim().length < MIN_PHRASE_LEN) {
    throw new Error(`recovery phrase must be at least ${MIN_PHRASE_LEN} characters`);
  }
  if (!(deviceSalt instanceof Uint8Array) || deviceSalt.length !== 32) throw new Error('escrow: bad salt');
  const kdfSalt = crypto.getRandomValues(new Uint8Array(16));
  const key = await phraseKey(phrase.trim(), kdfSalt, PBKDF2_ITERS);
  const envelope = await encrypt(key, deviceSalt, aadFor(userId));
  return { v: 1, kdf: 'PBKDF2-SHA-256', iters: PBKDF2_ITERS, kdfSalt: b64uEncode(kdfSalt), envelope: b64uEncode(envelope) };
}

/** Unwrap a salt-escrow blob. Throws on a wrong phrase / tampering / wrong user. */
export async function unwrapSalt(phrase, blob, userId) {
  if (!blob || blob.v !== 1) throw new Error('escrow: unsupported blob');
  const key = await phraseKey(String(phrase || '').trim(), b64uDecode(blob.kdfSalt), Number(blob.iters) || PBKDF2_ITERS);
  return decrypt(key, b64uDecode(blob.envelope), aadFor(userId));
}

// ── I/O paths (presign + S3) ─────────────────────────────────────────────────

async function presign(params) {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('escrow: not signed in');
  const url = params.op === 'put'
    ? `${cfg.apiBaseUrl}/backup/presign`
    : `${cfg.apiBaseUrl}/backup/presign?op=get&scope=escrow`;
  const res = await fetch(url, params.op === 'put'
    ? { method: 'POST', headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' }, body: JSON.stringify({ kind: 'salt-escrow' }) }
    : { headers: { authorization: `Bearer ${at}` } });
  if (!res.ok) throw new Error(`escrow: presign ${res.status}`);
  const body = await res.json();
  if (!body.url) throw new Error('escrow: malformed presign');
  return body.url;
}

/** Set (or replace) the recovery phrase: wraps this device's salt and uploads it. */
export async function setupRecoveryPhrase(phrase) {
  const claims = await currentClaims();
  if (!claims) throw new Error('escrow: not signed in');
  // Ensure the device has a crypto identity — deriveKeys lazily creates the
  // salt on first use (nothing else may have needed it yet on this device).
  await deriveKeys(claims.sub, kv);
  const deviceSalt = await kv.get(SALT_KEY);
  if (!(deviceSalt instanceof Uint8Array) || deviceSalt.length !== 32) {
    throw new Error('escrow: could not establish a device crypto identity');
  }
  const blob = await wrapSalt(phrase, deviceSalt, claims.sub);
  const url = await presign({ op: 'put' });
  const res = await fetch(url, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(blob) });
  if (!res.ok) throw new Error(`escrow: PUT failed ${res.status}`);
  return { ok: true };
}

// ── Books-encryption orchestration (solo end-to-end encryption) ─────────────
// Enable = one atomic user action: set the phrase (escrows the salt — the
// recoverability invariant) → flip the profile flag → re-upload the snapshot,
// now encrypted. "Encrypted but unrecoverable" is unrepresentable: the flag
// only turns on after the escrow PUT succeeded. Sharing overrides the flag at
// upload time (see sharedSnapshot.booksEncryptionActive) — inviting a viewer
// makes the books service-readable again, with consent copy in the invite flow.

export async function enableBooksEncryption(phrase) {
  const claims = await currentClaims();
  if (!claims) throw new Error('escrow: not signed in');
  await setupRecoveryPhrase(phrase); // throws on weak phrase / upload failure
  const existing = (await db.userProfile.get(claims.sub)) || { userId: claims.sub };
  await db.userProfile.put({ ...existing, userId: claims.sub, encryptBooks: true });
  const { shareSnapshotNow } = await import('./sharedSnapshot.js');
  await shareSnapshotNow(); // replace the plaintext blob immediately
  return { ok: true };
}

export async function disableBooksEncryption() {
  const claims = await currentClaims();
  if (!claims) throw new Error('escrow: not signed in');
  const existing = (await db.userProfile.get(claims.sub)) || { userId: claims.sub };
  await db.userProfile.put({ ...existing, userId: claims.sub, encryptBooks: false });
  const { shareSnapshotNow } = await import('./sharedSnapshot.js');
  await shareSnapshotNow(); // replace the encrypted blob with plaintext
  return { ok: true };
}

/** Is the encryptBooks flag set (regardless of team state)? Drives the Account UI. */
export async function booksEncryptionEnabled() {
  const claims = await currentClaims();
  if (!claims) return false;
  const prof = await db.userProfile.get(claims.sub).catch(() => null);
  return prof?.encryptBooks === true;
}

/**
 * On a NEW device: fetch the escrow blob, unwrap it with the phrase, and
 * install the salt locally so deriveKeys() reproduces the original backup key.
 * The caller then runs restorer.performRestore(). Refuses to overwrite an
 * existing local salt (that would orphan any backup THIS device already made).
 */
export async function recoverWithPhrase(phrase, { force = false } = {}) {
  const claims = await currentClaims();
  if (!claims) throw new Error('escrow: not signed in');
  const existing = await kv.get(SALT_KEY);
  if (existing instanceof Uint8Array && !force) {
    throw new Error('escrow: this device already has a crypto identity — pass force to replace it');
  }
  const url = await presign({ op: 'get' });
  const res = await fetch(url);
  if (res.status === 404 || res.status === 403) throw new Error('escrow: no recovery phrase was set up');
  if (!res.ok) throw new Error(`escrow: GET failed ${res.status}`);
  const blob = await res.json();
  let salt;
  try { salt = await unwrapSalt(phrase, blob, claims.sub); }
  catch { throw new Error('escrow: wrong recovery phrase'); }
  await kv.set(SALT_KEY, salt);
  return { ok: true };
}

/** Whether an escrow blob exists for this user (drives the Account UI copy). */
export async function escrowExists() {
  try {
    const url = await presign({ op: 'get' });
    const res = await fetch(url, { method: 'GET' });
    return res.ok;
  } catch { return false; }
}
